import React, { useState, useEffect } from 'react';
import { pyqAPI, architectAPI } from '../services/api';
import toast from 'react-hot-toast';

export default function PYQUpload() {
  const [file, setFile] = useState(null);
  const [exam, setExam] = useState('CLAT');
  const [year, setYear] = useState(new Date().getFullYear());
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [pyqDatabase, setPyqDatabase] = useState({});
  const [loadingPYQs, setLoadingPYQs] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState('all');

  useEffect(() => {
    loadPYQDatabase();
  }, []);

  const loadPYQDatabase = async () => {
    setLoadingPYQs(true);
    try {
      // Try to fetch PYQ stats from API
      try {
        const response = await pyqAPI.getPYQStats();
        setPyqDatabase(response.data || {});
      } catch (apiError) {
        // If endpoint doesn't exist, fetch approved questions and group them
        const response = await architectAPI.getApprovedQuestions();
        const questions = response.data?.questions || [];
        
        // Group questions by exam and year
        const pyqData = {};
        
        questions.forEach(question => {
          const examType = question.exam || 'UNKNOWN';
          const year = question.year || new Date().getFullYear();
          const topic = question.topic || 'General';
          
          if (!pyqData[examType]) {
            pyqData[examType] = {};
          }
          
          if (!pyqData[examType][year]) {
            pyqData[examType][year] = {
              count: 0,
              topics: new Set(),
              status: 'complete'
            };
          }
          
          pyqData[examType][year].count++;
          pyqData[examType][year].topics.add(topic);
        });
        
        // Convert Sets to Arrays
        Object.keys(pyqData).forEach(exam => {
          Object.keys(pyqData[exam]).forEach(year => {
            pyqData[exam][year].topics = Array.from(pyqData[exam][year].topics);
          });
        });
        
        setPyqDatabase(pyqData);
      }
    } catch (error) {
      console.error('Failed to load PYQ database:', error);
      toast.error('Failed to load PYQ database');
      // Set empty data on error
      setPyqDatabase({});
    } finally {
      setLoadingPYQs(false);
    }
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.type === 'application/pdf') {
      setFile(selectedFile);
      setResult(null);
    } else {
      toast.error('Please select a valid PDF file');
    }
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error('Please select a file first');
      return;
    }

    setUploading(true);
    const toastId = toast.loading('Uploading PYQ...');

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('exam', exam);
      formData.append('year', year);

      const response = await pyqAPI.upload(formData);
      setResult(response.data);
      toast.success(`PYQ uploaded! ${response.data.questions_ingested || 0} questions ingested`, { id: toastId });
      setFile(null);
      document.getElementById('pyq-file-input').value = '';
      // Reload PYQ database after successful upload
      await loadPYQDatabase();
    } catch (error) {
      toast.error(`Upload failed: ${error.response?.data?.message || error.message}`, { id: toastId });
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="min-h-screen">
      {/* Modern Header */}
      <div className="relative bg-gradient-to-r from-teal-600 via-cyan-700 to-blue-800 rounded-3xl shadow-2xl p-8 mb-8 overflow-hidden">
        {/* Decorative Elements */}
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/10 rounded-full blur-3xl"></div>
        
        <div className="relative">
          <div className="flex items-center space-x-4 mb-6">
            <div className="bg-white/20 backdrop-blur-sm rounded-2xl p-4 shadow-xl">
              <svg className="h-12 w-12 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
            </div>
            <div className="text-white">
              <h2 className="text-3xl font-bold mb-1">PYQ Upload 📝</h2>
              <p className="text-white/90">
                Upload Previous Year Questions to improve generation quality
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Exam Selection */}
            <div>
              <label className="block text-sm font-bold text-white mb-2">
                📚 Select Exam
              </label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => setExam('CLAT')}
                  className={`px-4 py-3 rounded-xl font-bold transition-all ${
                    exam === 'CLAT'
                      ? 'bg-white text-teal-700 shadow-lg scale-105'
                      : 'bg-white/20 text-white hover:bg-white/30'
                  }`}
                >
                  CLAT
                </button>
                <button
                  onClick={() => setExam('CUET')}
                  className={`px-4 py-3 rounded-xl font-bold transition-all ${
                    exam === 'CUET'
                      ? 'bg-white text-teal-700 shadow-lg scale-105'
                      : 'bg-white/20 text-white hover:bg-white/30'
                  }`}
                >
                  CUET
                </button>
              </div>
            </div>

            {/* Year Input */}
            <div>
              <label htmlFor="year" className="block text-sm font-bold text-white mb-2">
                📅 Year
              </label>
              <input
                type="number"
                id="year"
                value={year}
                onChange={(e) => setYear(parseInt(e.target.value))}
                min="2010"
                max={new Date().getFullYear()}
                className="block w-full rounded-xl border-2 border-white/30 bg-white/20 backdrop-blur-sm text-white placeholder-white/60 shadow-sm focus:border-white focus:ring-2 focus:ring-white/50 px-4 py-3 font-semibold"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Upload Section */}
      <div className="bg-white shadow-xl rounded-2xl p-6 mb-8 border border-gray-100">
        <div className="space-y-6">

          {/* Upload Area */}
          <div className="border-2 border-dashed border-gray-300 rounded-xl p-6 bg-gradient-to-br from-gray-50 to-white">
            <div className="text-center">
              <svg
                className="mx-auto h-16 w-16 text-teal-500"
                stroke="currentColor"
                fill="none"
                viewBox="0 0 48 48"
              >
                <path
                  d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                  strokeWidth={2}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <div className="mt-4">
                <label
                  htmlFor="pyq-file-input"
                  className="cursor-pointer inline-flex items-center px-6 py-3 rounded-xl bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700 text-white font-bold shadow-lg transition-all hover:scale-105"
                >
                  <svg className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                  📤 Upload PYQ PDF
                  <input
                    id="pyq-file-input"
                    type="file"
                    className="sr-only"
                    accept=".pdf"
                    onChange={handleFileChange}
                    disabled={uploading}
                  />
                </label>
              </div>
              <p className="text-sm text-gray-500 mt-3">PDF up to 20MB</p>
            </div>

            {file && (
              <div className="mt-6 flex items-center justify-center">
                <div className="flex items-center bg-teal-50 rounded-xl px-6 py-3 border-2 border-teal-200">
                  <svg className="h-6 w-6 text-teal-600 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <div>
                    <div className="text-sm font-bold text-gray-900">{file.name}</div>
                    <div className="text-xs text-gray-600">{exam} • {year}</div>
                  </div>
                  {!uploading && (
                    <button
                      onClick={() => {
                        setFile(null);
                        document.getElementById('pyq-file-input').value = '';
                      }}
                      className="ml-4 text-red-600 hover:text-red-800"
                    >
                      <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Upload Button */}
          <div className="flex justify-center">
            <button
              onClick={handleUpload}
              disabled={!file || uploading}
              className="inline-flex items-center px-8 py-4 border-2 border-transparent text-base font-bold rounded-xl shadow-lg text-white bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700 transition-all hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
            >
              {uploading ? '⏳ Processing...' : '🚀 Upload PYQ'}
            </button>
          </div>

          {/* Result */}
          {result && (
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-200 rounded-2xl p-6">
              <div className="flex items-start">
                <div className="bg-green-500 rounded-xl p-3 mr-4">
                  <svg className="h-6 w-6 text-white" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-gray-900 mb-2">✅ PYQ Uploaded Successfully!</h3>
                  <p className="font-semibold text-gray-700">📝 Questions ingested: {result.questions_ingested || 0}</p>
                  <p className="text-sm text-gray-600 mt-1">Collection: <code className="bg-green-100 px-2 py-1 rounded">exam_archives_{exam.toLowerCase()}</code></p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      {/* PYQ Database - Categorized View */}
      <div className="bg-white shadow-xl rounded-2xl overflow-hidden border border-gray-100">
        <div className="bg-gradient-to-r from-gray-50 via-teal-50 to-cyan-50 px-6 py-4 border-b-2 border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-xl font-bold text-gray-900 flex items-center">
              <svg className="h-6 w-6 mr-2 text-teal-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
              </svg>
              🗄️ PYQ Database - Categorized by Exam & Year
            </h3>
            <div className="flex gap-2">
              <button
                onClick={() => setSelectedFilter('all')}
                className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
                  selectedFilter === 'all'
                    ? 'bg-teal-600 text-white shadow-md'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                All
              </button>
              <button
                onClick={() => setSelectedFilter('CLAT')}
                className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
                  selectedFilter === 'CLAT'
                    ? 'bg-teal-600 text-white shadow-md'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                CLAT Only
              </button>
              <button
                onClick={() => setSelectedFilter('CUET')}
                className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
                  selectedFilter === 'CUET'
                    ? 'bg-teal-600 text-white shadow-md'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                CUET Only
              </button>
            </div>
          </div>
        </div>
        
        <div className="p-6">
          {loadingPYQs ? (
            <div className="flex justify-center items-center p-12">
              <div className="relative">
                <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-b-4 border-teal-600"></div>
                <div className="absolute top-0 left-0 h-12 w-12 rounded-full bg-teal-100"></div>
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              {Object.entries(pyqDatabase)
                .filter(([examType]) => selectedFilter === 'all' || selectedFilter === examType)
                .map(([examType, years]) => (
                  <div key={examType} className="border-2 border-gray-200 rounded-xl overflow-hidden">
                    {/* Exam Header */}
                    <div className={`px-6 py-4 ${
                      examType === 'CLAT' 
                        ? 'bg-gradient-to-r from-blue-500 to-indigo-600' 
                        : 'bg-gradient-to-r from-purple-500 to-pink-600'
                    }`}>
                      <h4 className="text-2xl font-bold text-white flex items-center">
                        <svg className="h-8 w-8 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                        </svg>
                        {examType}
                        <span className="ml-auto text-sm font-normal text-white/90">
                          {Object.keys(years).length} year(s) • {Object.values(years).reduce((sum, y) => sum + y.count, 0)} total questions
                        </span>
                      </h4>
                    </div>
                    
                    {/* Years Grid */}
                    <div className="p-6 bg-gray-50 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {Object.entries(years)
                        .sort(([yearA], [yearB]) => parseInt(yearB) - parseInt(yearA))
                        .map(([year, data]) => (
                          <div key={year} className="group bg-white border-2 border-gray-200 hover:border-teal-300 rounded-xl p-4 transition-all hover:shadow-lg">
                            <div className="flex items-start justify-between mb-3">
                              <div className="flex items-center">
                                <div className={`w-12 h-12 rounded-xl flex items-center justify-center font-bold text-white ${
                                  examType === 'CLAT' 
                                    ? 'bg-gradient-to-br from-blue-500 to-indigo-600' 
                                    : 'bg-gradient-to-br from-purple-500 to-pink-600'
                                } shadow-md`}>
                                  {year.slice(-2)}
                                </div>
                                <div className="ml-3">
                                  <div className="text-lg font-bold text-gray-900">{year}</div>
                                  <div className="text-xs text-gray-500">{examType}</div>
                                </div>
                              </div>
                              {data.status === 'complete' ? (
                                <span className="inline-flex items-center px-2 py-1 rounded-lg text-xs font-semibold bg-green-100 text-green-800">
                                  <svg className="h-3 w-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                  </svg>
                                  Complete
                                </span>
                              ) : (
                                <span className="inline-flex items-center px-2 py-1 rounded-lg text-xs font-semibold bg-yellow-100 text-yellow-800">
                                  Partial
                                </span>
                              )}
                            </div>
                            
                            <div className="mb-3">
                              <div className="text-3xl font-bold text-teal-600">{data.count}</div>
                              <div className="text-sm text-gray-500">questions</div>
                            </div>
                            
                            <div className="space-y-1">
                              <div className="text-xs font-semibold text-gray-700 mb-1">Topics:</div>
                              <div className="flex flex-wrap gap-1">
                                {data.topics.map((topic, idx) => (
                                  <span key={idx} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700">
                                    {topic}
                                  </span>
                                ))}
                              </div>
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>
                ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
